<template>
  <div v-if="showTemperature" class="opportunity-admin-panel">
    <header class="opportunity-admin-panel__header">
      <span class="opportunity-admin-panel__header-title">{{ $t('lang.label.admin_panel_title') }}</span>
      <span
        v-if="statusBadge"
        :class="['badge badge-pill opportunity-admin-panel__status-badge', statusBadge.class]"
      >
        {{ statusBadge.label }}
      </span>
    </header>

    <!-- Température de l'opportunité -->
    <section
      v-if="showTemperature"
      class="opportunity-admin-panel__section"
    >
      <h6 class="opportunity-admin-panel__section-title">
        <i class="icon icon-thermometer-half" aria-hidden="true"></i>
        {{ $t('lang.label.admin_panel_temperature_section') }}
      </h6>
      <div class="opportunity-admin-panel__temperature-card">
        <div
          class="opportunity-admin-panel__temperature-icon-wrap"
          :style="{ backgroundColor: temperatureColors.bg }"
        >
          <i
            :class="['icon', temperatureIcon]"
            :style="{ color: temperatureIconColor }"
            aria-hidden="true"
          ></i>
        </div>
        <div class="opportunity-admin-panel__temperature-body">
          <span class="opportunity-admin-panel__temperature-label">{{ $t('lang.label.admin_panel_temperature_current_status') }}</span>
          <span class="opportunity-admin-panel__temperature-value">{{ temperatureLabel }}</span>
        </div>
        <!-- Calculé automatiquement — sin lógica en app por ahora
        <span class="opportunity-admin-panel__temperature-auto">
          <i class="icon-star" aria-hidden="true"></i>
          Calculé automatiquement
        </span>
        -->
      </div>
    </section>

    <!-- Tâches en cours -->
    <Tasks
      v-if="permissionFront"
      :link="opportunity"
      :bdr-name="opportunity.link.bdr_name"
      :from-admin-tasks="fromAdminTasks"
      :opportunity_id="opportunityId"
      :key="tasksKey"
      @pendingTasks="$emit('pending-tasks', $event)"
      @taskSelected="$emit('task-selected', $event)"
      @listOfTasks="$emit('list-of-tasks', $event)"
    />

    <button
      v-if="showCheckAllTasks"
      type="button"
      class="opportunity-admin-panel__check-all-btn"
      @click="$emit('check-all-tasks')"
    >
      {{ $t('lang.label.admin_panel_check_all_tasks') }}
    </button>

    <section
      v-if="meetingPreviewRecord"
      class="opportunity-admin-panel__section opportunity-admin-panel__section--meeting"
    >
      <h6 class="opportunity-admin-panel__section-title">
        <i class="icon icon-bar-chart" aria-hidden="true"></i>
        {{ $t("lang.meeting_recording.title") }}
      </h6>
      <div class="opportunity-admin-panel__meeting-wrap">
        <MeetingAnalysisPreviewCard
          :record="meetingPreviewRecord"
          @open-analysis="$emit('open-meeting-analysis', $event)"
        />
      </div>
    </section>
    
    <!-- Outils du booker -->
    <section
      v-if="showBookerTools"
      class="opportunity-admin-panel__section"
    >
      <h6 class="opportunity-admin-panel__section-title">
        <i class="icon icon-wrench" aria-hidden="true"></i>
        {{ $t('lang.label.admin_panel_booker_tools_section') }}
      </h6>
      <div class="opportunity-admin-panel__booker-tools">
        <button
          v-if="showAiScript"
          type="button"
          class="opportunity-admin-panel__tool-card"
          @click="$emit('open-aiscript')"
        >
          <span class="opportunity-admin-panel__tool-icon opportunity-admin-panel__tool-icon--script">
            <i class="icon icon-star" aria-hidden="true"></i>
          </span>
          <span class="opportunity-admin-panel__tool-text">
            <strong>{{ $t('lang.label.admin_panel_ai_script_title') }}</strong>
            <small>{{ $t('lang.label.admin_panel_ai_script_subtitle') }}</small>
          </span>
        </button>
        <button
          v-if="showCalendar"
          type="button"
          class="opportunity-admin-panel__tool-card"
          @click="$emit('open-calendar')"
        >
          <span class="opportunity-admin-panel__tool-icon opportunity-admin-panel__tool-icon--calendar">
            <i class="icon icon-calendar" aria-hidden="true"></i>
          </span>
          <span class="opportunity-admin-panel__tool-text">
            <strong>{{ $t('lang.label.admin_panel_calendar_title') }}</strong>
            <small>{{ $t('lang.label.admin_panel_calendar_subtitle') }}</small>
          </span>
        </button>
        <button
          v-if="showCopyCalendarLink"
          type="button"
          class="opportunity-admin-panel__tool-card"
          @click="copyCalendarLink(getCalendarLink())"
        >
          <span class="opportunity-admin-panel__tool-icon opportunity-admin-panel__tool-icon--copy">
            <i class="icon icon-clipboard" aria-hidden="true"></i>
          </span>
          <span class="opportunity-admin-panel__tool-text">
            <strong>{{ $t('lang.label.admin_panel_copy_calendar_title') }}</strong>
            <small>{{ $t('lang.label.admin_panel_copy_calendar_subtitle') }}</small>
          </span>
        </button>
      </div>
    </section>

    <!-- Séquence de prospection -->
    <OportunityTopArea
      v-if="permissionFront && !isViewAdminTask"
      :business="opportunity.business"
      :business-contacts="opportunity.business_contacts"
      :status="opportunity.status"
      :link="opportunity.link"
      :events="opportunity.events"
      :unlocked="opportunity.unlocked"
      @doNotContactBusiness="$emit('do-not-contact-business', $event)"
      @unsuscribeBusiness="$emit('unsuscribe-business', $event)"
      @deleteBusiness="$emit('delete-business', $event)"
      @positiveBusiness="$emit('positive-business', $event)"
      @negativeBusiness="$emit('negative-business', $event)"
      @unlockContacts="$emit('unlock-contacts', $event)"
      @reloadOp="$emit('reload-op', $event)"
      @reloadTasks="$emit('reload-tasks', $event)"
    />

    <!-- Lier un événement -->
    <section
      v-if="showLinkEvent"
      class="opportunity-admin-panel__section"
    >
      <h6 class="opportunity-admin-panel__section-title">
        <i class="icon icon-link" aria-hidden="true"></i>
        {{ $t('lang.label.admin_panel_link_event_section') }}
      </h6>
      <div class="opportunity-admin-panel__link-event">
        <p class="opportunity-admin-panel__link-event-hint">
          {{ $t('lang.label.admin_panel_link_event_hint') }}
        </p>
        <div class="opportunity-admin-panel__link-event-row">
          <input
            type="text"
            class="opportunity-admin-panel__link-input"
            :value="linkIdToAttach"
            :placeholder="$t('lang.label.admin_panel_link_event_placeholder')"
            @input="$emit('update:linkIdToAttach', $event.target.value)"
          />
          <button
            type="button"
            class="opportunity-admin-panel__link-btn"
            :disabled="!linkIdToAttach"
            @click="$emit('attach-event')"
          >
            {{ $t('lang.label.admin_panel_link_event_btn') }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Tasks from "@/components/Opportunities/SingleOpportunity/Tasks.vue";
import MeetingAnalysisPreviewCard from "@/components/Opportunities/SingleOpportunity/MeetingAnalysisPreviewCard.vue";
import OportunityTopArea from "@/components/Common/OportunityTopArea.vue";

const TEMPERATURE_BG_COLORS = [
  { id: 1, labelKey: "admin_panel_temp_urgent", bgColor: "#008000", icon: "icon-fire" },
  { id: 2, labelKey: "admin_panel_temp_positive", bgColor: "#008000", icon: "icon-sun-o" },
  { id: 3, labelKey: "admin_panel_temp_introduction", bgColor: "#0000FF", icon: "icon-thermometer-full" },
  { id: 4, labelKey: "admin_panel_temp_wrong_contact", bgColor: "#0000FF", icon: "icon-thermometer-half" },
  { id: 5, labelKey: "admin_panel_temp_negative", bgColor: "#0000FF", icon: "icon-thermometer-empty" },
  { id: 6, labelKey: "admin_panel_temp_calendar_inspection", bgColor: "#00FFFF", icon: "icon-calendar-check-o" },
  { id: 7, labelKey: "admin_panel_temp_reschedule", bgColor: "#FFFF00", icon: "icon-calendar" },
  { id: 8, labelKey: "admin_panel_temp_cold", bgColor: "#C0C0C0", icon: "icon-snowflake-o" },
  { id: 9, labelKey: "admin_panel_temp_other_answer", bgColor: "#FF9966", icon: "icon-comment-o" },
];

export default {
  name: "OpportunityAdminPanel",
  components: {
    Tasks,
    MeetingAnalysisPreviewCard,
    OportunityTopArea,
  },
  props: {
    opportunity: {
      type: Object,
      required: true,
    },
    fromAdminTasks: {
      type: Boolean,
      default: false,
    },
    tasksKey: {
      type: [String, Number],
      default: 0,
    },
    opportunityId: {
      type: Number,
      default: null,
    },
    isViewAdminTask: {
      type: Boolean,
      default: false,
    },
    permissionFront: {
      type: Boolean,
      default: true,
    },
    linkIdToAttach: {
      type: String,
      default: "",
    },
    meetingPreviewRecord: {
      type: Object,
      default: null,
    },
    statusBadge: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.authentication.user,
    }),
    isBdrUser() {
      const user = this.currentUser;
      return user && user.team_role_id == 2 && user.role_id == 2;
    },
    showTemperature() {
      // ESTA LINEA ESTA BLOQUEANDO LEADROOM.
      // if (this.fromAdminTasks) return false;
      const roleId = this.currentUser && this.currentUser.role_id ? this.currentUser.role_id : null;
      return roleId != 4 && roleId != 3;
    },
    showCheckAllTasks() {
      return this.currentUser && this.currentUser.role_id == 1;
    },
    showBookerTools() {
      return this.showAiScript || this.showCalendar || this.showCopyCalendarLink;
    },
    showAiScript() {
      return this.currentUser && this.currentUser.role_id == 1;
    },
    showCalendar() {
      return (
        this.currentUser &&
        (this.currentUser.role_id == 1 || this.isBdrUser)
      );
    },
    showCopyCalendarLink() {
      return (
        this.currentUser &&
        this.currentUser.role_id == 2 &&
        this.opportunity &&
        this.opportunity.campaignTokens &&
        this.opportunity.campaignTokens.my_calendar_link &&
        this.opportunity.campaignTokens.my_calendar_link.value
      );
    },
    showLinkEvent() {
      return this.currentUser && this.currentUser.role_id == 1;
    },
    temperatureEntry() {
      const id = this.opportunity.link.link_status_temperature_id;
      return (
        TEMPERATURE_BG_COLORS.find((e) => e.id == id) || {
          bgColor: "#e5e7eb",
          icon: "icon-thermometer-half",
        }
      );
    },
    temperatureLabel() {
      const entry = this.temperatureEntry;
      if (entry.labelKey) {
        return this.$t(`lang.label.${entry.labelKey}`);
      }
      return "—";
    },
    temperatureIcon() {
      return this.temperatureEntry.icon;
    },
    temperatureColors() {
      return {
        bg: this.temperatureEntry.bgColor,
      };
    },
    temperatureIconColor() {
      return this.isLightBackground(this.temperatureEntry.bgColor)
        ? "#374151"
        : "#ffffff";
    },
  },
  methods: {
    isLightBackground(hex) {
      if (!hex) return true;
      const normalized = hex.replace("#", "");
      if (normalized.length !== 6) return true;
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.55;
    },
    copyCalendarLink(calendar_link) {
      if (this.copyToClipboard(calendar_link)) {
        this.$swal({
          type: "success",
          title: this.$t("lang.label.great"),
          text: this.$t("lang.general.copiedtoclipboard"),
          showConfirmButton: false,
          timer: 1500,
        });
      }
    },
    getCalendarLink() {
      return (
        this.opportunity.campaignTokens.my_calendar_link.value +
        "/" +
        this.convertBase64("linkid:" + this.opportunity.link.id)
      );
    },
    copyToClipboard(text) {
      if (window.clipboardData && window.clipboardData.setData) {
        return window.clipboardData.setData("Text", text);
      }
      if (
        document.queryCommandSupported &&
        document.queryCommandSupported("copy")
      ) {
        const textarea = document.createElement("textarea");
        textarea.textContent = text;
        textarea.style.position = "fixed";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          return document.execCommand("copy");
        } catch (ex) {
          console.warn("Copy to clipboard failed.", ex);
          return false;
        } finally {
          document.body.removeChild(textarea);
        }
      }
      return false;
    },
    convertBase64(txt) {
      return btoa(txt);
    },
  },
};
</script>

<style scoped>
.opportunity-admin-panel {
  --ol-brand: #5054eb;
  --ol-brand-soft: rgb(229, 231, 235);
  --ol-brand-border: rgb(229, 231, 235);
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid var(--ol-brand-border);
  overflow: hidden;
  margin-bottom: 1rem;
  isolation: isolate;
}

/* Evita que iconos icomoon (checkbox/check) se dibujen fuera de su caja y tapen el header */
.opportunity-admin-panel i[class*="icon-"]::before {
  line-height: 1;
  display: block;
  overflow: hidden;
}

.opportunity-admin-panel__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--ol-brand-soft);
  border-bottom: 1px solid var(--ol-brand-border);
  position: relative;
  z-index: 2;
  overflow: hidden;
  isolation: isolate;
}

.opportunity-admin-panel__header-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ol-brand);
  margin: 0;
}

.opportunity-admin-panel__status-badge {
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  color: #fff;
}

.opportunity-admin-panel__status-badge.admin-meeting-badge-absent {
  background-color: #fd7e14;
}

.opportunity-admin-panel__section {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.opportunity-admin-panel__section:last-child {
  border-bottom: none;
}

.opportunity-admin-panel__section-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #6b7280;
  margin: 0 0 0.75rem;
  text-transform: uppercase;
}

.opportunity-admin-panel__section-title i {
  font-size: 0.85rem;
  color: var(--ol-brand);
}

.opportunity-admin-panel__temperature-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
}

.opportunity-admin-panel__temperature-icon-wrap {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.92;
}

.opportunity-admin-panel__temperature-icon-wrap i {
  font-size: 1.15rem;
}

.opportunity-admin-panel__temperature-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.opportunity-admin-panel__temperature-label {
  font-size: 0.7rem;
  color: #9ca3af;
}

.opportunity-admin-panel__temperature-value {
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ol-brand);
}

.opportunity-admin-panel__check-all-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: calc(100% - 2rem);
  margin: 0 1rem 1rem;
  padding: 0.6rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  background: #ffffff;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.opportunity-admin-panel__check-all-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.opportunity-admin-panel__booker-tools {
  display: flex;
  gap: 0.75rem;
}

.opportunity-admin-panel__tool-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, box-shadow 0.15s;
}

.opportunity-admin-panel__tool-card:hover {
  background: #f3f4f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.opportunity-admin-panel__tool-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.opportunity-admin-panel__tool-icon--script {
  background: var(--ol-brand-soft);
  color: var(--ol-brand);
}

.opportunity-admin-panel__tool-icon--calendar {
  background: #d1fae5;
  color: #047857;
}

.opportunity-admin-panel__tool-icon--copy {
  background: #ede9fe;
  color: #6d28d9;
}

.opportunity-admin-panel__tool-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.opportunity-admin-panel__tool-text strong {
  font-size: 0.85rem;
  color: #111827;
}

.opportunity-admin-panel__tool-text small {
  font-size: 0.7rem;
  color: #9ca3af;
}

.opportunity-admin-panel__link-event {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem;
}

.opportunity-admin-panel__link-event-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0 0 0.75rem;
}

.opportunity-admin-panel__link-event-row {
  display: flex;
  gap: 0.5rem;
}

.opportunity-admin-panel__link-input {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
}

.opportunity-admin-panel__link-input:focus {
  outline: none;
  border-color: var(--ol-brand);
  box-shadow: 0 0 0 2px var(--ol-brand-soft);
}

.opportunity-admin-panel__link-btn {
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  background: var(--ol-brand);
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.opportunity-admin-panel__link-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.opportunity-admin-panel__link-btn:not(:disabled):hover {
  background: #3f43d4;
}

.opportunity-admin-panel__section--meeting {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.opportunity-admin-panel__meeting-wrap {
  min-width: 0;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card {
  border-radius: 8px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__header {
  padding: 10px 12px;
  gap: 8px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__header-icon {
  width: 32px;
  height: 32px;
  font-size: 14px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__title {
  font-size: 13px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__subtitle {
  font-size: 12px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__body--done {
  padding: 12px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__body--centered {
  padding: 20px 12px 24px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__meta {
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__meta-item {
  white-space: normal;
  font-size: 12px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__engagement {
  padding: 10px 12px;
  margin-bottom: 10px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__score-value {
  font-size: 26px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__summary-block {
  margin-bottom: 12px;
}

.opportunity-admin-panel__meeting-wrap >>> .map-card__cta {
  width: 100%;
}
</style>



